import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  signInWithRedirect,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  type SignInOutput,
} from 'aws-amplify/auth';

export interface AuthConfig {
  userPoolId: string;
  userPoolClientId: string;
  region?: string;
  // OAuth configuration for federated sign-in
  oauth?: {
    domain: string;
    scopes: string[];
    redirectSignIn: string[];
    redirectSignOut: string[];
    responseType: 'code' | 'token';
  };
}

export interface AuthUser {
  userId: string;
  email: string;
  groups: string[];
}

export interface SignInResult {
  isSignedIn: boolean;
  nextStep?: {
    signInStep: string;
  };
}

/**
 * Configure Amplify Auth with Cognito settings
 */
export function configureAuth(config: AuthConfig): void {
  const authConfig: {
    Cognito: {
      userPoolId: string;
      userPoolClientId: string;
      signUpVerificationMethod: 'code';
      loginWith?: {
        oauth?: {
          domain: string;
          scopes: string[];
          redirectSignIn: string[];
          redirectSignOut: string[];
          responseType: 'code' | 'token';
        };
      };
    };
  } = {
    Cognito: {
      userPoolId: config.userPoolId,
      userPoolClientId: config.userPoolClientId,
      signUpVerificationMethod: 'code',
    },
  };

  // Add OAuth config if provided (for federated sign-in)
  if (config.oauth) {
    authConfig.Cognito.loginWith = {
      oauth: config.oauth,
    };
  }

  Amplify.configure({ Auth: authConfig });
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  const result: SignInOutput = await amplifySignIn({
    username: email,
    password,
  });

  return {
    isSignedIn: result.isSignedIn,
    nextStep: result.nextStep ? { signInStep: result.nextStep.signInStep } : undefined,
  };
}

/**
 * Complete sign-in with new password (for first login)
 */
export async function completeNewPassword(newPassword: string): Promise<SignInResult> {
  const result = await confirmSignIn({
    challengeResponse: newPassword,
  });

  return {
    isSignedIn: result.isSignedIn,
    nextStep: result.nextStep ? { signInStep: result.nextStep.signInStep } : undefined,
  };
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  await amplifySignOut();
}

/**
 * Get the current authenticated user
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const user = await amplifyGetCurrentUser();
    const session = await fetchAuthSession();

    // Extract groups from the ID token
    const idToken = session.tokens?.idToken;
    const groups = (idToken?.payload?.['cognito:groups'] as string[]) || [];

    return {
      userId: user.userId,
      email: user.signInDetails?.loginId || '',
      groups,
    };
  } catch {
    return null;
  }
}

/**
 * Get the current access token for API calls
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Check if there is a valid authenticated session
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens?.accessToken;
  } catch {
    return false;
  }
}

/**
 * Initiate password reset flow
 */
export async function forgotPassword(email: string): Promise<void> {
  await resetPassword({ username: email });
}

/**
 * Complete password reset with confirmation code
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

/**
 * Sign in with a federated identity provider (e.g., QuickBooks)
 * This redirects to the provider's login page
 */
export function signInWithProvider(provider: string): void {
  signInWithRedirect({
    provider: { custom: provider },
  });
}

export interface SignUpResult {
  isSignUpComplete: boolean;
  userId?: string;
  nextStep: {
    signUpStep: string;
    codeDeliveryDetails?: {
      deliveryMedium: string;
      destination: string;
    };
  };
}

/**
 * Sign up a new user with email and password
 */
export async function signUp(
  email: string,
  password: string,
  attributes?: { name?: string }
): Promise<SignUpResult> {
  const result = await amplifySignUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        ...(attributes?.name && { name: attributes.name }),
      },
    },
  });

  // Extract code delivery details if available (only present for CONFIRM_SIGN_UP step)
  const nextStep = result.nextStep as {
    signUpStep: string;
    codeDeliveryDetails?: { deliveryMedium?: string; destination?: string };
  };

  return {
    isSignUpComplete: result.isSignUpComplete,
    userId: result.userId,
    nextStep: {
      signUpStep: nextStep.signUpStep,
      codeDeliveryDetails: nextStep.codeDeliveryDetails
        ? {
            deliveryMedium: nextStep.codeDeliveryDetails.deliveryMedium || 'EMAIL',
            destination: nextStep.codeDeliveryDetails.destination || email,
          }
        : undefined,
    },
  };
}

/**
 * Confirm sign up with verification code sent to email
 */
export async function confirmSignUp(email: string, code: string): Promise<boolean> {
  const result = await amplifyConfirmSignUp({
    username: email,
    confirmationCode: code,
  });
  return result.isSignUpComplete;
}

/**
 * Resend sign up confirmation code
 */
export async function resendSignUpCode(email: string): Promise<void> {
  await amplifyResendSignUpCode({ username: email });
}

/**
 * Get custom attributes from the ID token (e.g., QB realm ID)
 */
export async function getCustomAttribute(attributeName: string): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    return (idToken?.payload?.[attributeName] as string) || null;
  } catch {
    return null;
  }
}
